/**
 * The 1st and 99th percentiles of `values`, as [low, high].
 *
 * A COUNT is trimmed from each end, not an index taken as a fraction of
 * the length: indexing by fraction can trim one end and not the other.
 * Below 100 values this trims nothing, a hundredth of them being less
 * than one value, so the range is the minimum and maximum: the honest
 * behaviour of a 1% trim on a short input.
 *
 * The values are sorted in place, so callers pass a buffer they own. An
 * explicit numeric comparator is given because Array.prototype.sort
 * defaults to lexicographic order.
 *
 * @throws if `values` is empty.
 */
export function trimmedRange(
    values: number[] | Float32Array
): [number, number] {
    if (values.length === 0) {
        throw new Error('trimmedRange: no values');
    }
    values.sort((a, b) => a - b);
    const trim = Math.floor(values.length * 0.01);
    return [values[trim], values[values.length - 1 - trim]];
}

/**
 * A display window covering the middle 98% of `values`, as
 * [centre, width].
 *
 * Percentiles rather than the extremes ({@link trimmedRange}): one
 * saturated voxel (a marker, a fiducial, a metal artefact) would
 * otherwise set the width and leave the anatomy in a narrow band of greys.
 *
 * The values are sorted in place, so callers pass a buffer they own. No
 * modality is assumed: the window comes from the data, so a CT in
 * Hounsfield units and an MR on an arbitrary scale both read correctly.
 */
export function percentileWindow(
    values: number[] | Float32Array
): [number, number] {
    if (values.length === 0) {
        return [0, 400];
    }

    const [lo, hi] = trimmedRange(values);

    // A uniform region has no width to show; one keeps the division safe
    // and renders it flat, which is what it is.
    const width = Math.max(1, hi - lo);
    return [lo + width / 2, width];
}

/** Values examined when summarising a whole volume. */
const MAX_VOLUME_SAMPLES = 200000;

/**
 * Evenly spaced values of a volume, in a new array the caller owns.
 *
 * A volume runs to tens of millions of voxels and a summary of its
 * intensities only has to be representative, so a fixed budget of samples
 * decides it. The stride is derived from the length, so the same volume
 * always yields the same samples.
 */
export function sampleVolumeAtStride(voxels: ArrayLike<number>): number[] {
    const stride = Math.max(1, Math.floor(voxels.length / MAX_VOLUME_SAMPLES));
    const values: number[] = [];
    for (let i = 0; i < voxels.length; i += stride) {
        values.push(voxels[i]);
    }
    return values;
}

/**
 * {@link percentileWindow} over a volume, sampled by
 * {@link sampleVolumeAtStride}. The same volume always yields the same
 * window: a preview whose contrast shifted between renders would look
 * like the image had changed.
 */
export function autoWindowFromVolume(pixels: Int16Array): [number, number] {
    return percentileWindow(sampleVolumeAtStride(pixels));
}
